import { Button, SortableList } from "@southfields-digital/mxmz-react-ui";
import { useCallback, useMemo } from "react";

import { type Member, MemberTypes } from "src/types/matchControl/member";
import MemberRow from "./MemberRow";
import type { MemberColumn } from "./memberColumns";

type Props = {
	members: Member[];
	columns: readonly MemberColumn[];
	setMembers: (members: Member[]) => void;
};

const MembersList = ({ members, columns, setMembers }: Props) => {
	const handleAddMember = () => {
		const newMember: Member = {
			name: "",
			number: String(members.length + 1), // Auto-increment number based on current length
			type: MemberTypes.STARTER,
		};
		setMembers([...members, newMember]);
	};

	const handleUpdateMember = useCallback(
		(updatedMember: Member, index: number) => {
			const updatedMembers = [...members];
			updatedMembers[index] = updatedMember;
			setMembers(updatedMembers);
		},
		[members, setMembers],
	);

	const handleDeleteMember = useCallback(
		(index: number) => {
			const updatedMembers = members.filter((_, i) => i !== index);
			setMembers(updatedMembers);
		},
		[members, setMembers],
	);

	const handleChangeOrderMembers = (orderedMembers: Member[]) => {
		setMembers(orderedMembers);
	};

	const items = useMemo(
		() =>
			members.map((member, index) => ({
				id: index.toString(),
				member,
				columns,
				handleDeleteMember: () => handleDeleteMember(index),
				handleUpdateMember: (updatedMember: Member) => handleUpdateMember(updatedMember, index),
			})),
		[members, columns, handleDeleteMember, handleUpdateMember],
	);

	return (
		<div className="flex flex-col gap-2 relative w-full">
			<SortableList
				items={items}
				setItems={(items) => handleChangeOrderMembers(items.map((item) => item.member))}
				Component={MemberRow}
			/>
			<Button onClick={handleAddMember} variant="secondary">
				Add Member
			</Button>
		</div>
	);
};

export default MembersList;
